import { FC, Fragment, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Tabs,
	Typography,
} from 'antd';
import {
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	ArrowLeftOutlined,
	DownloadOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import { useModals } from '@/components/modals/ModalProvider';
import dayjs from 'dayjs';
import { formatCurrency, formatCurrencyWithDot } from '@/libs/antd/formater';
import { NAPNJFModal, useNAPNJFModal } from '../../nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '../../nap/NAPJFDownloadPage';
import KolektabilitasModal, {
	useKolektabilitasModal,
} from '@/pages/mpp/KolektabilitasModal';
import { usePrintPurchaseOrder } from '@/libs/fleet-api/hooks/purchase-order';
import {
	PrintVerificationRequestPayload,
	usePrintVerificationApprovalMutation,
	usePrintVerificationDetailQuery,
	usePrintVerificationRequestMutation,
} from '@/libs/fleet-api/services/print-verification';
import rule from '@/libs/antd/rules';
import { useUserData } from '@/libs/fleet-api/hooks';
import Checkbox from 'antd/es/checkbox/Checkbox';
import CovenantModal, { useCovenantModal } from '../disbursement/CovenantModal';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import { MPPDetail } from '@/libs/fleet-api/services/mpp';
import UploadDocumentModal, {
	UploadDocumentModalData,
	useUploadDocumentModal,
} from './UploadDocumentModal';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { FileData } from '@/libs/fleet-api/services/types';
import { useApp } from '@/App';

type PrintVerificationRequestForm = Omit<
	PrintVerificationRequestPayload,
	'disbursementBillingDocuments'
> & {
	action?: string;
	returnUser?: { id: number; value: string };
	disbursementBillingDocuments: {
		purchaseOrderId: number;
		billings: (UploadDocumentModalData & { unitSeq: number })[];
		disbursements: (UploadDocumentModalData & { unitSeq: number })[];
	}[];
	covenants?: {
		id: number;
		status: string;
		additionalInformation: string;
	}[];
};
const covenantStatusOptions = [
	{
		id: 'done',
		value: 'Done',
	},
	{
		id: 'pending',
		value: 'Pending',
	},
] as unknown as MasterData[];

const VerifyDisbursementPage: FC<{
	mppId: number;
	financingProductId: number;
	disbursementId?: number;
	applicationIds?: string[];
	onBack?: () => void;
	viewOnly?: boolean;
}> = ({ mppId, financingProductId, disbursementId, applicationIds, onBack, viewOnly }) => {
	const isViewOnly = viewOnly === true;
	const { navigate } = useApp();
	const userData = useUserData();
	const { groupName } = userData ?? {};
	const {
		modal,
		openCustomerDetail,
		openCustomerGroupDetail,
		openPreviewModal,
		openPreviewDocumentModal,
		openDocumentModal,
	} = useModals();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();
	const covenantModal = useCovenantModal();
	const kolektabilitasModal = useKolektabilitasModal();
	const uploadDocumentModal = useUploadDocumentModal();

	const { data, isFetching } = usePrintVerificationDetailQuery({
		mppId,
		financingProductId,
		disbursementId,
		applicationIds,
	});

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const printPurchaseOrderMutation = usePrintPurchaseOrder();

	const printVerificationRequestMutation =
		usePrintVerificationRequestMutation();

	const printVerificationApprovalMutation =
		usePrintVerificationApprovalMutation();

	const mainForm = useForm<PrintVerificationRequestForm>();

	const [view, setView] = useState<
		| {
				type: 'main';
		  }
		| {
				type: 'view';
				data?: MPPDetail['transactionDetailSummary'][number];
		  }
	>({
		type: 'main',
	});

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const {
		id,
		fcasName,
		fcdvsName,
		approvalHistory,
		returnUsers = [],
		mppDetails,
		agreementInformation,
		billingInformations = [],
		realizationDocumentList,
		specialConditionDocumentList,
		goliveConditionDocumentList,
	} = data?.data ?? {};

	const {
		customerName,
		customerId,
		bmppGroupName,
		bmppGroupId,
		industryClass,
		portfolioGuideline,
		ultimateOwner,
		mklNo,
		covenants = [],
		facilities,
		agingDate,
		transactionDetailSummary,
		facilityNotification,
		prospectId,
		customerFacility,
		customerFacilitySummary,
		customerAffiliatesFacility,
		customerAffiliateFacilitySummary,
		obligorDetail,
		napPlafonTypeId,
		resolutionFile,
		backgroundRealisation,
		financingLimitTypeOptions = [],
		plafonTypeOptions = [],
		wayOfPaymentOptions = [],
		orderSource,
		spfNo,
		spfId,
		napResolutionId,
		slikInquiryId,
	} = mppDetails ?? {};

	const renderMainForm = () => {
		const { form, fieldName, value } = mainForm;

		const totalCustomerSubmission =
			(customerFacilitySummary?.totalNtf ?? 0) -
			(customerFacilitySummary?.totalOutstanding ?? 0);

		const totalPlafondApproved = customerFacilitySummary?.totalPlafon ?? 0;
		const totalRemainingPlafon =
			totalPlafondApproved - (customerFacilitySummary?.totalNtf ?? 0);

		const billingItems = billingInformations.map((billing, billingIndex) => {
			const billingColumns = (
				billing.billingDocumentDetails?.[0]?.billingUploadDocuments ?? []
			).map((doc) => {
				return {
					title: doc.name,
					align: 'center',
					render: (_: unknown, __: unknown, index: number) => {
						const { billings = [] } =
							value?.disbursementBillingDocuments?.[billingIndex] ?? {};

						// First, check if this document should be merged by finding it in any billing
						const documentInAnyBilling = billings
							.flatMap((b) => b.uploadDocuments || [])
							.find((d) => d.masterDocumentId === doc.masterDocumentId);

						const shouldMergeDocument =
							(documentInAnyBilling as { isMultipleUnit?: boolean })
								?.isMultipleUnit && billings.length > 1;

						// For merged documents, always use the document from the first billing that has it
						// For single unit documents, use the document from the current billing
						const uploadDocument = shouldMergeDocument
							? billings
									.flatMap((b) => b.uploadDocuments || [])
									.find((d) => d.masterDocumentId === doc.masterDocumentId)
							: billings?.[index]?.uploadDocuments?.find(
									(d) => d.masterDocumentId === doc.masterDocumentId,
								);

						if (shouldMergeDocument) {
							// Only render content in the first row, merge all other rows
							if (index === 0) {
								// Check if document exists and has URL
								const content = !uploadDocument?.document?.url ? (
									<FileSearchOutlined
										style={{ color: '#bababa' }}
										title="Document not available"
									/>
								) : (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewDocumentModal({
												url: uploadDocument.document.url,
											});
										}}
										title={`Click to view ${doc.name}`}
									>
										<FileSearchOutlined />
									</a>
								);

								return {
									children: content,
									props: {
										rowSpan: billings.length || 1,
									},
								};
							}
							// Return null for other rows to merge them
							return {
								children: null,
								props: {
									rowSpan: 0,
								},
							};
						}

						// Regular rendering for non-merged documents
						// Check if document exists and has URL
						if (!uploadDocument?.document?.url) {
							return (
								<FileSearchOutlined
									style={{ color: '#bababa' }}
									title="Document not available"
								/>
							);
						}

						// Document is clickable if it exists and has URL
						return (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openPreviewDocumentModal({
										url: uploadDocument.document.url,
									});
								}}
								title={`Click to view ${doc.name}`}
							>
								<FileSearchOutlined />
							</a>
						);
					},
				};
			});
			const disbursementColumns = (
				billing?.disbursementRequirementDocumentDetails?.[0]
					?.disbursementUploadDocuments ?? []
			).map((doc) => {
				return {
					title: doc.name,
					align: 'center',

					render: (_: unknown, __: unknown, index: number) => {
						const { disbursements = [] } =
							value?.disbursementBillingDocuments?.[billingIndex] ?? {};

						// First, check if this document should be merged by finding it in any unit
						const documentInAnyUnit = disbursements
							.flatMap((d) => d.uploadDocuments || [])
							.find((d) => d.masterDocumentId === doc.masterDocumentId);

						const shouldMergeDocument =
							(documentInAnyUnit as { isMultipleUnit?: boolean })
								?.isMultipleUnit && disbursements.length > 1;

						// For merged documents, always use the document from the first unit that has it
						// For single unit documents, use the document from the current unit
						const uploadDocument = shouldMergeDocument
							? disbursements
									.flatMap((d) => d.uploadDocuments || [])
									.find((d) => d.masterDocumentId === doc.masterDocumentId)
							: disbursements?.[index]?.uploadDocuments?.find(
									(d) => d.masterDocumentId === doc.masterDocumentId,
								);

						if (shouldMergeDocument) {
							// Only render content in the first row, merge all other rows
							if (index === 0) {
								// Check if document exists and has URL
								const content = !uploadDocument?.document?.url ? (
									<FileSearchOutlined
										style={{ color: '#bababa' }}
										title="Document not available"
									/>
								) : (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewDocumentModal({
												url: uploadDocument.document.url,
											});
										}}
										title={`Click to view ${doc.name}`}
									>
										<FileSearchOutlined />
									</a>
								);

								return {
									children: content,
									props: {
										rowSpan: disbursements.length,
									},
								};
							}
							// Return null for other rows to merge them
							return {
								children: null,
								props: {
									rowSpan: 0,
								},
							};
						}

						// Regular rendering for non-merged documents
						// Check if document exists and has URL
						if (!uploadDocument?.document?.url) {
							return (
								<FileSearchOutlined
									style={{ color: '#bababa' }}
									title="Document not available"
								/>
							);
						}

						// Document is clickable if it exists and has URL
						return (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openPreviewDocumentModal({
										url: uploadDocument.document.url,
									});
								}}
								title={`Click to view ${doc.name}`}
							>
								<FileSearchOutlined />
							</a>
						);
					},
				};
			});

			return {
				label: `Informasi Tagihan ${billingIndex + 1}`,
				children: (
					<Fragment>
						<div>
							<Row
								gutter={20}
								wrap={false}
							>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>No. Simulasi</b>
										</Col>
										<Col span={12}>
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													const simulationId =
														billingInformations[billingIndex].simulationId;
													if (!simulationId) return;
													navigate('SimulationPage', {
														simulationId,
														showResult: true,
														viewOnly: true,
														onBack: () => {
															navigate('VerifyDisbursementPage', {
																mppId,
																financingProductId,
																disbursementId,
																applicationIds,
																onBack,
															});
														},
													});
												}}
											>
												{billingInformations[billingIndex].simulationNo}
											</a>
										</Col>
									</Row>
								</Col>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>No. PO</b>
										</Col>
										<Col span={12}>
											<a
												href="#"
												onClick={async (e) => {
													e.preventDefault();
													const { purchaseOrderId } =
														billingInformations[billingIndex] ?? {};
													if (!purchaseOrderId) return;

													const documents =
														await printPurchaseOrderMutation.mutateAsync({
															purchaseOrderIds: [purchaseOrderId],
														});

													openDocumentModal(documents);
												}}
											>
												{billingInformations[billingIndex].purchaseOrderNo}
											</a>
										</Col>
									</Row>
								</Col>
							</Row>
							<Divider />
							<Row
								gutter={20}
								wrap={false}
							>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>Dealer</b>
										</Col>
										<Col span={12}>
											{billingInformations[billingIndex].dealerName}
										</Col>
									</Row>
								</Col>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>Jumlah Unit</b>
										</Col>
										<Col span={12}>
											{billingInformations[billingIndex].unitCount}
										</Col>
									</Row>
								</Col>
							</Row>
							<Divider />
							<Row
								gutter={20}
								wrap={false}
							>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>Contact Person</b>
										</Col>
										<Col span={12}>
											{billingInformations[billingIndex].contactPerson}
										</Col>
									</Row>
								</Col>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>Supplier Phone</b>
										</Col>
										<Col span={12}>
											{billingInformations[billingIndex].supplierPhoneNo}
										</Col>
									</Row>
								</Col>
							</Row>
							<Divider />
							<Row
								gutter={20}
								wrap={false}
							>
								<Col span={12}>
									<Row>
										<Col span={12}>
											<b>A/P to Account</b>
										</Col>
										<Col span={12}>
											{billingInformations[billingIndex].accountInformation}
										</Col>
									</Row>
								</Col>
							</Row>
							<Divider />
						</div>

						<FleetTitle style={{ marginBottom: 20 }}>
							Detail Agreement
						</FleetTitle>
						<Table
							bordered
							scroll={{ x: 'max-content' }}
							pagination={false}
							dataSource={
								billingInformations[billingIndex].estarAgreementDetails.map(
									(record, key) => ({
										...record,
										key,
									}),
								) ?? []
							}
							columns={[
								{
									title: 'No',
									align: 'center',
									fixed: 'left',
									width: 1,
									render: (_, __, index) => index + 1,
								},
								{
									title: 'No Agreement',
									render: (_, record) => record.estarAgreementNo ?? '-',
								},
								{
									title: 'Asset',
									render: (_, record) => record.assetName ?? '-',
								},
								{
									title: 'BAST/Delivery Date',
									render: (_, record) =>
										record.deliveryDate
											? dayjs(record.deliveryDate).format('DD/MM/YYYY')
											: '-',
								},
								{
									title: 'Buy Back Guarantee Valid Date',
									render: (_, record) =>
										record.buyBackGuaranteeValidity
											? dayjs(record.buyBackGuaranteeValidity).format(
													'DD/MM/YYYY',
												)
											: '-',
								},
								{
									title: 'Penerima',
									render: (_, record) => record.receiverName,
								},
								{
									title: 'Lokasi Penerima',
									render: (_, record) => record.receiverLocation,
								},
								{
									title: 'No. Chasis',
									render: (_, record) => record.chasisNo,
								},
								{
									title: 'No Engine',
									render: (_, record) => record.engineNo,
								},
								{
									title: 'Manufacturing Year',
									render: (_, record) => record.manufacturingYear,
								},
								{
									title: 'Color',
									render: (_, record) => record.color,
								},
								{
									title: 'License Plate',
									render: (_, record) => record.licensePlate,
								},
								{
									title: 'Nama',
									render: (_, record) => record.picName,
								},
								{
									title: 'Alamat',
									render: (_, record) => record.masterAddress,
								},
								{
									title: 'Tahun Berakhir Pajak Kendaraan',
									render: (_, record) => record.lastYearVehicleTax,
								},
								{
									title: 'Nama Pemilik BPKB Baru',
									render: (_, record) => record.bpkbName,
								},
								{
									title: 'Alamat Pemilik BPKB Baru',
									render: (_, record) => record.bpkbAddress,
								},
							]}
						/>
						<Divider />

						<FleetTitle style={{ marginBottom: 20 }}>
							{groupName === 'FCDVS'
								? 'Upload Dokumen Tagihan'
								: 'View Dokumen Tagihan'}
						</FleetTitle>
						<Form.Item
							name={fieldName([
								'disbursementBillingDocuments',
								billingIndex,
								'purchaseOrderId',
							])}
							initialValue={billing.purchaseOrderId}
							noStyle
						/>
						<Form.Item
							name={fieldName([
								'disbursementBillingDocuments',
								billingIndex,
								'billings',
							])}
							wrapperCol={{ span: 24 }}
							initialValue={billing.billingDocumentDetails.map((detail) => {
								return {
									unitSeq: detail.unitSequence,
									agreementNo: detail.estarAgreementNo,
									assetName: detail.assetName,
									uploadDocuments: detail.billingUploadDocuments,
								};
							})}
						>
							<Table
								bordered
								scroll={{ x: 'max-content' }}
								pagination={false}
								dataSource={
									value?.disbursementBillingDocuments?.[billingIndex]
										?.billings ?? []
								}
								columns={[
									{
										title: 'No',
										align: 'center',
										fixed: 'left',
										width: 1,
										render: (_, __, index) => index + 1,
									},
									{
										hidden: !value?.disbursementBillingDocuments?.[
											billingIndex
										]?.billings?.some((b) =>
											b.uploadDocuments.some((d) => d.allowUpload),
										),
										title: 'Edit',
										align: 'center',
										fixed: 'left',
										width: 1,
										render: (_, record, recordIndex) => {
											return (
												<a
													href="#"
													onClick={async (e) => {
														e.preventDefault();

														const updated = await uploadDocumentModal.open({
															title: 'Edit Detail Tagihan',
															customerId: customerId!,
															document: {
																...record,
																uploadDocuments: record.uploadDocuments.filter(
																	(d) => d.allowUpload,
																),
															},
														});

														if (!updated) return;
														form.setFieldValue(
															fieldName([
																'disbursementBillingDocuments',
																billingIndex,
																'billings',
															]),
															value?.disbursementBillingDocuments?.[
																billingIndex
															].billings.map((data, index) => {
																if (index !== recordIndex) return data;
																return {
																	...data,
																	uploadDocuments: data.uploadDocuments.map(
																		(existingDoc) => {
																			// Find the updated document with the same masterDocumentId
																			const updatedDoc =
																				updated.uploadDocuments.find(
																					(doc) =>
																						doc.masterDocumentId ===
																						existingDoc.masterDocumentId,
																				);
																			// If found, use the updated document, otherwise keep the existing one
																			return updatedDoc || existingDoc;
																		},
																	),
																};
															}),
														);
													}}
												>
													<EditOutlined />
												</a>
											);
										},
									},
									{
										title: 'No Agreement',
										render: (_, record) => record.agreementNo ?? '-',
									},
									{
										title: 'Asset',
										render: (_, record) => record.assetName,
									},
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									...(billingColumns as any),
								]}
							/>
						</Form.Item>
						<Divider />
						<FleetTitle style={{ marginBottom: 20 }}>
							{groupName === 'FCDVS'
								? 'Upload Dokumen Syarat Pencairan'
								: 'View Dokumen Syarat Pencairan'}
						</FleetTitle>
						<Form.Item
							name={fieldName([
								'disbursementBillingDocuments',
								billingIndex,
								'disbursements',
							])}
							wrapperCol={{ span: 24 }}
							initialValue={billing.disbursementRequirementDocumentDetails.map(
								(detail) => {
									return {
										agreementNo: detail.estarAgreementNo,
										assetName: detail.assetName,
										uploadDocuments: detail.disbursementUploadDocuments,
									} as UploadDocumentModalData;
								},
							)}
						>
							<Table
								bordered
								scroll={{ x: 'max-content' }}
								pagination={false}
								dataSource={
									value?.disbursementBillingDocuments?.[billingIndex]
										.disbursements ?? []
								}
								columns={[
									{
										title: 'No',
										align: 'center',
										fixed: 'left',
										width: 1,
										render: (
											_: unknown,
											record: { unitSequence?: number },
											index: number,
										) => {
											// Use unitSequence if available, otherwise use index + 1
											const unitNumber = record.unitSequence || index + 1;

											// Always show unit number for each row without rowspan
											return unitNumber;
										},
									},
									{
										hidden: !value?.disbursementBillingDocuments?.[
											billingIndex
										]?.disbursements?.some((b) =>
											b.uploadDocuments.some((d) => d.allowUpload),
										),
										title: 'Edit',
										align: 'center',
										fixed: 'left',
										width: 1,
										render: (_, record, recordIndex) => {
											return (
												<a
													href="#"
													onClick={async (e) => {
														e.preventDefault();
														const updated = await uploadDocumentModal.open({
															title:
																groupName === 'FCDVS'
																	? 'Edit Dokumen Syarat Pencairan'
																	: 'Edit Detail Asset Document',
															customerId: customerId!,
															document: {
																...record,
																uploadDocuments: record.uploadDocuments.filter(
																	(d) => d.allowUpload,
																),
															},
														});

														if (!updated) return;
														form.setFieldValue(
															fieldName([
																'disbursementBillingDocuments',
																billingIndex,
																'disbursements',
															]),
															value?.disbursementBillingDocuments?.[
																billingIndex
															].disbursements.map((data, index) => {
																if (index !== recordIndex) return data;
																return {
																	...data,
																	uploadDocuments: data.uploadDocuments.map(
																		(existingDoc) => {
																			// Find the updated document with the same masterDocumentId
																			const updatedDoc =
																				updated.uploadDocuments.find(
																					(doc) =>
																						doc.masterDocumentId ===
																						existingDoc.masterDocumentId,
																				);
																			// If found, use the updated document, otherwise keep the existing one
																			return updatedDoc || existingDoc;
																		},
																	),
																};
															}),
														);
													}}
												>
													<EditOutlined />
												</a>
											);
										},
									},
									{
										title: 'No Agreement',
										render: (_, record) => record.agreementNo ?? '-',
									},
									{
										title: 'Asset',
										render: (_, record) => record.assetName,
									},
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									...(disbursementColumns as any),
								]}
							/>
						</Form.Item>
					</Fragment>
				),
			};
		});

		return (
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				hidden={view.type !== 'main'}
				labelCol={{ span: 10 }}
				wrapperCol={{ span: 14 }}
				onFinish={async (values) => {
					const isConfirmed = await modal.confirm({
						title: 'Apakah dokumen pencairan yang di inputkan sudah sesuai?',
						okText: 'YA',
						cancelText: 'TIDAK',
					});

					if (!isConfirmed) return;

					switch (groupName) {
						case 'FCDVS': {
							await printVerificationRequestMutation.mutateAsync({
								disbursementId: id!,
								applicationIds,
								disbursementBillingDocuments:
									values.disbursementBillingDocuments.map((d) => {
										return {
											purchaseOrderId: d.purchaseOrderId,
											billingDocuments: d.billings
												.map((bil) => bil.uploadDocuments)
												.flat()
												.map((bil) => {
													return {
														unitSeq: bil.unitSequence,
														document: bil.document,
														masterDisbursementDocumentId: bil.masterDocumentId,
													};
												}),
											disbursementDocuments: d.disbursements
												.map((bil) => bil.uploadDocuments)
												.flat()
												.map((bil) => {
													return {
														unitSeq: bil.unitSequence,
														document: bil.document,
														masterDisbursementDocumentId: bil.masterDocumentId,
													};
												}),
										};
									}),
								disbursementRealizationDocuments:
									values.disbursementRealizationDocuments,
								disbursementGoliveConditionDocuments:
									values.disbursementGoliveConditionDocuments,
								disbursementSpecialConditionDocuments:
									values.disbursementSpecialConditionDocuments,
								notes: values.notes,
							});

							navigate('VerifyDisbursementListPage');
							break;
						}
						case 'FCOH': {
							await printVerificationApprovalMutation.mutateAsync({
								action: values.action!,
								notes: values.notes,
								returnUser:
									values.action! === 'return'
										? values.returnUser?.id
										: undefined,
								disbursementId: id!,
								covenants:
									values.covenants?.map((covenant) => {
										return {
											id: covenant.id!,
											additionalInformation:
												covenant.additionalInformation ?? undefined,
											statusId: covenant.status ?? undefined,
										};
									}) ?? [],
								disbursementBillingDocuments:
									values.disbursementBillingDocuments.map((d) => {
										return {
											purchaseOrderId: d.purchaseOrderId,
											billingDocuments: d.billings
												.map((bil) => bil.uploadDocuments)
												.flat()
												.map((bil) => {
													return {
														unitSeq: bil.unitSequence,
														document: bil.document,
														masterDisbursementDocumentId: bil.masterDocumentId,
													};
												}),
											disbursementDocuments: d.disbursements
												.map((bil) => bil.uploadDocuments)
												.flat()
												.map((bil) => {
													return {
														unitSeq: bil.unitSequence,
														document: bil.document,
														masterDisbursementDocumentId: bil.masterDocumentId,
													};
												}),
										};
									}),
							});
							navigate('VerifyDisbursementListPage');
							break;
						}
						case 'FCOM': {
							await printVerificationApprovalMutation.mutateAsync({
								action: values.action!,
								returnUser:
									values.action! === 'return'
										? values.returnUser?.id
										: undefined,
								notes: values.notes,
								disbursementId: id!,
								covenants:
									values.covenants?.map((covenant) => {
										return {
											id: covenant.id!,
											additionalInformation:
												covenant.additionalInformation ?? undefined,
											statusId: covenant.status ?? undefined,
										};
									}) ?? [],
								disbursementBillingDocuments:
									values.disbursementBillingDocuments.map((billing) => {
										return {
											purchaseOrderId: billing.purchaseOrderId,
											billingDocuments: [],
											disbursementDocuments: [],
										};
									}),
							});
							navigate('VerifyDisbursementListPage');
							break;
						}
					}
				}}
			>
				<Collapse
					defaultActiveKey={new Array(5 + billingInformations.length)
						.fill(0)
						.map((_, index) => index)}
					items={[
						{
							label: 'Informasi Dokumen Covenant',
							children: (
								<Fragment>
									<Form.Item noStyle>
										<Table
											scroll={{ x: 'max-content' }}
											bordered
											pagination={false}
											dataSource={
												covenants?.map((record, index) => {
													return {
														...record,
														key: index + 1,
													};
												}) ?? []
											}
											columns={[
												{
													title: 'No',
													fixed: 'left',
													align: 'center',
													render: (_, __, index) => index + 1,
												},
												// {
												// 	title: 'Edit',
												// 	align: 'center',
												// 	hidden: groupName !== 'FCOH' && groupName !== 'FCOM',
												// 	render: (_, record, index) => {
												// 		// Only show edit button for FCOH group, when covenant has document, and monitoringType.id === 1 (Syarat Golive)
												// 		const originalCovenant = covenants?.[index];
												// 		if (
												// 			(groupName !== 'FCOH' && groupName !== 'FCOM') ||
												// 			!originalCovenant?.covenantDocument
												// 		) {
												// 			return (
												// 				<EditOutlined style={{ color: '#bababa' }} />
												// 			);
												// 		}

												// 		return (
												// 			<a
												// 				onClick={async (e) => {
												// 					e.preventDefault();
												// 					const updatedCovenant =
												// 						await covenantModal.open({
												// 							...record,
												// 							id: record.id,
												// 							additionalInformation: record.notes,
												// 							statusId: record.statusId,
												// 						});

												// 					if (!updatedCovenant) return;
												// 					form.setFieldValue(
												// 						fieldName('covenants'),
												// 						(value?.covenants ?? []).map(
												// 							(record, recordIdx) =>
												// 								index === recordIdx
												// 									? updatedCovenant
												// 									: record,
												// 						),
												// 					);
												// 				}}
												// 			>
												// 				<EditOutlined />
												// 			</a>
												// 		);
												// 	},
												// },
												{
													title: 'Covenant',
													render: (_, record) => record.covenant,
												},
												{
													title: 'Kategori',
													render: (_, record) => record.category?.value ?? '-',
												},
												{
													title: 'Priority',
													render: (_, record) => record.priority?.value ?? '-',
												},
												{
													title: 'Repeat',
													render: (_, record) =>
														record.repeatFrequency?.value ?? '-',
												},
												{
													title: 'Jenis Monitoring',
													render: (_, record) =>
														record.monitoringType?.value ?? '-',
												},
												{
													title: 'Analisa Lebih Lanjut',
													render: (_, record) =>
														record.furtherAnalysis?.value ?? '-',
												},
												{
													title: 'Tanggal Upload Dokumen',
													render: (_, record) =>
														record.uploadDocumentDate
															? dayjs(record.uploadDocumentDate).format(
																	'DD/MM/YYYY',
																)
															: '-',
												},
												{
													title: 'View Dokumen',
													fixed: 'right',
													align: 'center',
													render: (_, record) => (
														<a
															onClick={(e) => {
																e.preventDefault();
																if (!record.covenantDocument?.id) return;
																openPreviewModal(record.covenantDocument);
															}}
														>
															<FileSearchOutlined
																style={{
																	color: record.covenantDocument?.id
																		? '#1557BC'
																		: '#bababa',
																}}
															/>
														</a>
													),
												},
												{
													title: 'Status',
													render: (_, record) =>
														covenantStatusOptions.find(
															(option) => String(option.id) === record.status,
														)?.value ?? '-',
												},
												{
													title: 'Due Date',
													render: (_, record) =>
														record.dueDate
															? dayjs(record.dueDate).format('DD/MM/YYYY')
															: '-',
												},
												{
													title: 'OD',
													render: (_, __, recordIndex) => {
														const od = covenants?.find(
															(_, index) => index === recordIndex,
														)?.overdueDays;

														return typeof od === 'number' ? `${od} Hari` : '-';
													},
												},
												{
													title: 'Keterangan',
													render: (_, record) => record.notes ?? '-',
												},
												// {
												// 	title: 'Keterangan FCOH',
												// 	render: (_, record) =>
												// 		record.additionalInformation ?? '-',
												// },
											]}
										/>
									</Form.Item>
									{/* Warning messages for covenants with monitoringType id === 1 that need to be 'done' */}
									{(groupName === 'FCOH' || groupName === 'FCOM') &&
										(() => {
											const incompleteCovenants =
												covenants?.filter((covenant) => {
													return (
														covenant.monitoringType?.id === 1 &&
														covenant.covenantDocument?.id &&
														covenant.status !== 'done'
													);
												}) ?? [];

											if (incompleteCovenants.length > 0) {
												return (
													<Alert
														type="warning"
														showIcon
														style={{
															marginTop: 16,
														}}
														className="custom-alert"
														message={
															<span style={{ fontWeight: 'bold' }}>
																{incompleteCovenants.length} Dokumen covenant
																debitur belum lengkap
															</span>
														}
														description={null}
													/>
												);
											}
											return null;
										})()}
								</Fragment>
							),
						},
						{
							label: 'Informasi Approval Pemberitahuan Fasilitas',
							children: (
								<Fragment>
									{facilityNotification?.map((record, index) => {
										const isMultiguna = record.financingTypes.some(
											(d) => d.value === 'Pembiayaan Multiguna',
										);

										return (
											<Fragment key={index}>
												<div style={{ marginBottom: 10 }}>
													<strong>Limit Plafon {index + 1}</strong>
												</div>
												<Table
													bordered
													pagination={false}
													dataSource={
														[
															{
																subject: 'Jenis Pembiayaan',
																approval:
																	record.financingTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Jenis Fasilitas',
																approval:
																	record.facilityTypes
																		?.map((type) => type.value)
																		?.join(', ') ?? '-',
															},
															{
																subject: 'Limit Pembiayaan',
																approval: record.requestedLimit
																	? formatCurrency(record.requestedLimit)
																	: '-',
															},
															{
																subject: 'Sifat Limit Pembiayaan',
																approval:
																	financingLimitTypeOptions?.find(
																		(option) =>
																			option.id === record.financingLimitTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Jenis Plafon',
																approval:
																	plafonTypeOptions?.find(
																		(option) =>
																			option.id === record.plafonTypeId,
																	)?.value ?? '-',
															},
															{
																subject: 'Way Of Payment',
																approval:
																	wayOfPaymentOptions?.find(
																		(option) =>
																			option.id === record.wayOfPaymentId,
																	)?.value ?? '-',
															},
															{
																subject: 'Masa Berlaku Fasilitas',
																approval: record.facilityValidityPeriod
																	? dayjs(record.facilityValidityPeriod).format(
																			'DD/MM/YYYY',
																		)
																	: '-',
															},
															...(isMultiguna
																? [
																		{
																			subject: 'Mekanisme Pencairan',
																			approval:
																				record.disbursementMechanism ?? '-',
																		},
																		{
																			subject: 'Kondisi Objek Pembiayaan',
																			approval:
																				record.financingObjectCondition ?? '-',
																		},
																		{
																			subject: 'Penilaian Objek Pembiayaan',
																			approval:
																				record.financingObjectEvaluation ?? '-',
																		},
																	]
																: []),
														] as Record<'subject' | 'approval', string>[]
													}
													columns={[
														{
															title: 'Perihal',
															width: '50%',
															render: (_, record) => record.subject,
														},
														{
															title: 'Keterangan Approval',
															width: '50%',
															render: (_, record) => <b>{record.approval}</b>,
														},
													]}
												/>
												<Divider />
											</Fragment>
										);
									})}
									<Typography.Title level={3}>
										Detail Fasilitas
									</Typography.Title>
									<Table
										dataSource={facilities ?? []}
										columns={[
											{
												title: 'No',
												align: 'center',
												render: (_, __, index: number) => index + 1,
											},
											{
												title: 'Jenis Asset',
												render: (_, record) => record.assetType?.value,
											},
											{
												title: 'Kondisi Obyek Pembiayaan',
												render: (_, record) =>
													record.financingObjectCondition?.value,
											},
											{
												title: 'Sifat Limit Pembiayaan',
												render: (_, record) =>
													record.financingLimitType?.value ?? '-',
											},
											{
												title: 'Jenis Plafon',
												render: (_, record) => record.plafonType?.value,
											},
											{
												title: 'Tenor Pembiayaan',
												render: (_, record) => record.tenor?.value,
											},
											{
												title: 'Indikasi Suku Bunga (Eff)',
												render: (_, record) =>
													record.interestRateIndicationPercent
														? `${formatCurrencyWithDot(record.interestRateIndicationPercent)}%`
														: '-',
											},
											{
												title: 'Down Payment',
												render: (_, record) =>
													record.downPaymentPercent
														? `${formatCurrencyWithDot(record.downPaymentPercent)}%`
														: '-',
											},
											{
												title: 'Security Deposit',
												render: (_, record) =>
													record.securityDeposit
														? formatCurrency(record.securityDeposit)
														: '-',
											},
											{
												title: 'Biaya Administrasi',
												render: (_, record) =>
													record.administrativeFee
														? formatCurrency(record.administrativeFee)
														: '-',
											},
											{
												title: 'Provisi',
												render: (_, record) =>
													record.provisionFee
														? formatCurrencyWithDot(record.provisionFee) + '%'
														: '-',
											},
											{
												title: 'Biaya Layanan',
												render: (_, record) =>
													record.serviceFee
														? `${formatCurrencyWithDot(record.serviceFee)}%`
														: '-',
											},
											{
												title: 'Asuransi',
												render: (_, record) => record.insurance?.value ?? '-',
											},
											{
												title: 'Diskon Asuransi',
												align: 'center',
												render: (_, record) =>
													record.insuranceDiscountPercentage
														? `${formatCurrencyWithDot(record.insuranceDiscountPercentage)}%`
														: '-',
											},
											{
												title: 'Fidusia',
												render: (_, record) =>
													record.fiduciaryFee
														? formatCurrency(record.fiduciaryFee)
														: '-',
											},
										]}
										bordered
										pagination={false}
										scroll={{ x: 'max-content' }}
									/>
								</Fragment>
							),
						},
						{
							label: 'Memo Pencairan Pembiayaan',
							children: (
								<Fragment>
									<Typography.Title level={3}>
										Latar Belakang Realisasi
									</Typography.Title>
									<Form.Item wrapperCol={{ span: 24 }}>
										<Input.TextArea
											rows={4}
											readOnly={true}
											value={backgroundRealisation}
										/>
									</Form.Item>
									<Divider />
									<div style={{ marginBottom: '20px', fontWeight: 'bold' }}>
										Informasi Realisasi
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												background: 'white',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: '20px',
											}}
										>
											<thead>
												<tr
													style={{ backgroundColor: '#2D4C82', color: 'white' }}
												>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Nama Customer
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Fasilitas
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														No. MPP
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														NTF
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														OS Pokok Hutang
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Angsuran/Bulan
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Jumlah Unit
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Detail PO Pending
													</th>
												</tr>
											</thead>
											<tbody>
												{(() => {
													// Get unique MPP numbers for existing facilities
													const existingFacilities =
														customerFacility?.existingFacilities || [];
													const uniqueExistingMppNos = Array.from(
														new Set(
															existingFacilities.map(
																(facility) => facility.mppNo || '',
															),
														),
													);

													// Sort pending facilities by MPP No first to ensure proper grouping
													const unsortedPendingFacilities =
														customerFacility?.pendingFacilities || [];
													const pendingFacilities = [
														...unsortedPendingFacilities,
													].sort((a, b) => {
														const aMppNo = a.mppNo || '';
														const bMppNo = b.mppNo || '';
														return aMppNo.localeCompare(bMppNo);
													});
													const uniquePendingMppNos = Array.from(
														new Set(
															pendingFacilities.map(
																(facility) => facility.mppNo || '',
															),
														),
													);

													// Always show at least one row for existing and pending
													const hasExistingRows =
														uniqueExistingMppNos.length > 0;
													const hasPendingRows = uniquePendingMppNos.length > 0;

													// If no existing facilities, add a placeholder MPP number
													if (!hasExistingRows) {
														uniqueExistingMppNos.push('');
													}

													// If no pending facilities, add a placeholder MPP number
													if (!hasPendingRows) {
														uniquePendingMppNos.push('');
													}

													return (
														<>
															{/* Existing Facilities - one row per facility */}
															{(() => {
																// Group facilities by MPP No and calculate rowspan
																const mppNoGroups = existingFacilities.reduce(
																	(groups, facility) => {
																		const mppNo = facility.mppNo || '';
																		if (!groups[mppNo]) {
																			groups[mppNo] = [];
																		}
																		groups[mppNo].push(facility);
																		return groups;
																	},
																	{} as Record<
																		string,
																		typeof existingFacilities
																	>,
																);

																return existingFacilities.map(
																	(facility, index) => {
																		const facilityNtf = facility.ntf ?? 0;
																		const facilityOutstanding =
																			facility.outstanding ?? 0;
																		const facilityInstallment =
																			facility.installment ?? 0;
																		const facilityUnitCount =
																			facility.unitCount ?? 0;
																		const facilityPoDetails =
																			facility.poNo || '';

																		const mppNo = facility.mppNo || '';
																		const facilitiesInGroup =
																			mppNoGroups[mppNo];
																		const facilityIndex =
																			facilitiesInGroup.indexOf(facility);
																		const isFirstInGroup = facilityIndex === 0;
																		const rowspan = facilitiesInGroup.length;
																		const showMppNoCell = isFirstInGroup;

																		return (
																			<tr key={`existing-${index}`}>
																				{/* Show customer name only for the first facility */}
																				{index === 0 && (
																					<>
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																								width: '160px',
																							}}
																						>
																							{customerName}
																						</td>
																						<td
																							rowSpan={
																								existingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								width: '80px',
																							}}
																						>
																							Eksisting
																						</td>
																					</>
																				)}
																				{showMppNoCell && (
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							color: '#1890ff',
																						}}
																						rowSpan={rowspan}
																					>
																						{facility.mppNo || '-'}
																					</td>
																				)}
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityNtf)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityOutstanding)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityInstallment)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{facilityUnitCount}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						whiteSpace: 'nowrap',
																					}}
																				>
																					{facilityPoDetails}
																				</td>
																			</tr>
																		);
																	},
																);
															})()}
															{/* Pending Facilities - one row per facility */}
															{(() => {
																// Group facilities by MPP No and calculate rowspan
																const mppNoGroups = pendingFacilities.reduce(
																	(groups, facility) => {
																		const mppNo = facility.mppNo || '';
																		if (!groups[mppNo]) {
																			groups[mppNo] = [];
																		}
																		groups[mppNo].push(facility);
																		return groups;
																	},
																	{} as Record<
																		string,
																		typeof pendingFacilities
																	>,
																);

																return pendingFacilities.map(
																	(facility, index) => {
																		const facilityNtf = facility.ntf ?? 0;
																		const facilityOutstanding =
																			facility.outstanding ?? 0;
																		const facilityInstallment =
																			facility.installment ?? 0;
																		const facilityUnitCount =
																			facility.unitCount ?? 0;
																		const facilityPoDetails =
																			facility.poNo || '';

																		const mppNo = facility.mppNo || '';
																		const facilitiesInGroup =
																			mppNoGroups[mppNo];
																		const facilityIndex =
																			facilitiesInGroup.indexOf(facility);
																		const isFirstInGroup = facilityIndex === 0;
																		const rowspan = facilitiesInGroup.length;
																		const showMppNoCell = isFirstInGroup;

																		return (
																			<tr key={`pending-${index}`}>
																				{/* Show customer name only if there are no existing facilities */}
																				{existingFacilities.length === 0 &&
																					index === 0 && (
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																								width: '160px',
																							}}
																						>
																							{customerName}
																						</td>
																					)}
																				{/* Show "Pending" label only for the first facility in each MPP group */}
																				{isFirstInGroup && (
																					<td
																						rowSpan={rowspan}
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																						}}
																					>
																						Pending
																					</td>
																				)}
																				{showMppNoCell && (
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							color: '#1890ff',
																						}}
																						rowSpan={rowspan}
																					>
																						{facility.mppNo || '-'}
																					</td>
																				)}
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityNtf)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityOutstanding)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{formatCurrency(facilityInstallment)}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						textAlign: 'right',
																					}}
																				>
																					{facilityUnitCount}
																				</td>
																				<td
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						whiteSpace: 'nowrap',
																					}}
																				>
																					{facilityPoDetails}
																				</td>
																			</tr>
																		);
																	},
																);
															})()}
														</>
													);
												})()}
												<tr>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														Pengajuan
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.totalNtf || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.totalOutstanding || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.installmentAmount || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{customerFacilitySummary?.totalUnitCount || ''}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													/>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.totalNtf || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.totalOutstanding || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerFacilitySummary?.installmentAmount || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{customerFacilitySummary?.totalUnitCount || ''}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													></td>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total Pengajuan Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{totalCustomerSubmission
															? formatCurrency(totalCustomerSubmission)
															: ''}
													</td>
													<td
														colSpan={4}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													/>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Plafond Customer Yang Disetujui
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{totalPlafondApproved
															? formatCurrency(totalPlafondApproved)
															: ''}
													</td>
													<td
														colSpan={4}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													/>
												</tr>
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Sisa Plafond Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{totalRemainingPlafon
															? formatCurrency(totalRemainingPlafon)
															: ''}
													</td>
													<td
														colSpan={4}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													/>
												</tr>
											</tbody>
										</table>
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												background: 'white',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: '20px',
											}}
										>
											<thead>
												<tr
													style={{ backgroundColor: '#2D4C82', color: 'white' }}
												>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Nama Customer
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Fasilitas
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														No. MPP
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														NTF
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														OS Pokok Hutang
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Angsuran/Bulan
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Jumlah Unit
													</th>
													<th
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'center',
															whiteSpace: 'nowrap',
														}}
													>
														Detail PO Pending
													</th>
												</tr>
											</thead>
											<tbody>
												{customerAffiliatesFacility?.map(
													(
														{
															customerAffiliateName,
															customerAffiliateFacilities,
														},
														affiliateIndex,
													) => {
														// Collect existing facilities with metadata
														const existingFacilities =
															customerAffiliateFacilities.flatMap(
																(facilityCollection, collectionIndex) => {
																	return (
																		facilityCollection.existingFacilities || []
																	).map((facility, facilityIndex) => ({
																		...facility,
																		type: 'existing' as const,
																		collectionIndex,
																		uniqueId: `${affiliateIndex}-${collectionIndex}-existing-${facilityIndex}`,
																	}));
																},
															);

														// Collect pending facilities with metadata
														const unsortedPendingFacilities =
															customerAffiliateFacilities.flatMap(
																(facilityCollection, collectionIndex) => {
																	return (
																		facilityCollection.pendingFacilities || []
																	).map((facility, facilityIndex) => ({
																		...facility,
																		type: 'pending' as const,
																		collectionIndex,
																		uniqueId: `${affiliateIndex}-${collectionIndex}-pending-${facilityIndex}`,
																	}));
																},
															);

														// Sort pending facilities by MPP No first to ensure proper grouping
														const pendingFacilities = [
															...unsortedPendingFacilities,
														].sort((a, b) => {
															const aMppNo = a.mppNo || '';
															const bMppNo = b.mppNo || '';
															return aMppNo.localeCompare(bMppNo);
														});

														// Group pending facilities by MPP No to ensure proper ordering
														const pendingByMppNo = pendingFacilities.reduce(
															(groups, facility) => {
																const mppNo = facility.mppNo || '';
																if (!groups[mppNo]) {
																	groups[mppNo] = [];
																}
																groups[mppNo].push(facility);
																return groups;
															},
															{} as Record<string, typeof pendingFacilities>,
														);

														// Sort pending facilities: group by MPP No
														const sortedPendingMppNos =
															Object.keys(pendingByMppNo).sort();

														// Calculate totals
														const totalPendingFacilities =
															pendingFacilities.length;
														const totalExistingFacilities =
															existingFacilities.length;
														const hasExistingFacilities =
															totalExistingFacilities > 0;

														return (
															<>
																{/* Render existing facilities first */}
																{existingFacilities.map((facility, index) => {
																	const facilityNtf = facility.ntf ?? 0;
																	const facilityOutstanding =
																		facility.outstanding ?? 0;
																	const facilityInstallment =
																		facility.installment ?? 0;
																	const facilityUnitCount =
																		facility.unitCount ?? 0;
																	const facilityPoDetails = facility.poNo || '';

																	const isFirstFacility = index === 0;
																	const mppNo = facility.mppNo || '';

																	return (
																		<tr key={facility.uniqueId}>
																			{/* Show customer name only for the first facility */}
																			{isFirstFacility && (
																				<td
																					rowSpan={
																						existingFacilities.length +
																						pendingFacilities.length
																					}
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																						verticalAlign: 'top',
																					}}
																				>
																					{customerAffiliateName}
																				</td>
																			)}
																			{/* Show "Eksisting" label only for the first existing facility */}
																			{isFirstFacility && (
																				<td
																					rowSpan={existingFacilities.length}
																					style={{
																						padding: '10px',
																						border: '1px solid #ccc',
																					}}
																				>
																					Eksisting
																				</td>
																			)}
																			{/* Show MPP No */}
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					color: '#1890ff',
																				}}
																			>
																				{mppNo || '-'}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityNtf)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityOutstanding)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{formatCurrency(facilityInstallment)}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					textAlign: 'right',
																				}}
																			>
																				{facilityUnitCount ?? 0}
																			</td>
																			<td
																				style={{
																					padding: '10px',
																					border: '1px solid #ccc',
																					whiteSpace: 'nowrap',
																				}}
																			>
																				{facilityPoDetails}
																			</td>
																		</tr>
																	);
																})}

																{/* Render pending facilities grouped by MPP No */}
																{sortedPendingMppNos.map((mppNo, mppIndex) => {
																	const facilitiesInGroup =
																		pendingByMppNo[mppNo];

																	return facilitiesInGroup.map(
																		(facility, index) => {
																			const facilityNtf = facility.ntf ?? 0;
																			const facilityOutstanding =
																				facility.outstanding ?? 0;
																			const facilityInstallment =
																				facility.installment ?? 0;
																			const facilityUnitCount =
																				facility.unitCount ?? 0;
																			const facilityPoDetails =
																				facility.poNo || '';

																			const isFirstInGroup = index === 0;
																			const isFirstPendingOverall =
																				mppIndex === 0 && index === 0;
																			const showCustomerName =
																				isFirstPendingOverall &&
																				!hasExistingFacilities;

																			return (
																				<tr key={facility.uniqueId}>
																					{/* Show customer name only if no existing facilities and this is the first pending facility */}
																					{showCustomerName && (
																						<td
																							rowSpan={
																								existingFacilities.length +
																								pendingFacilities.length
																							}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								verticalAlign: 'top',
																							}}
																						>
																							{customerAffiliateName}
																						</td>
																					)}
																					{/* Show "Pending" label only for the first pending facility overall */}
																					{isFirstPendingOverall && (
																						<td
																							rowSpan={totalPendingFacilities}
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																							}}
																						>
																							Pending
																						</td>
																					)}
																					{/* Show MPP No only for the first facility in this MPP group */}
																					{isFirstInGroup && (
																						<td
																							style={{
																								padding: '10px',
																								border: '1px solid #ccc',
																								color: '#1890ff',
																							}}
																							rowSpan={facilitiesInGroup.length}
																						>
																							{facility.mppNo || '-'}
																						</td>
																					)}
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{formatCurrency(facilityNtf)}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{formatCurrency(
																							facilityOutstanding,
																						)}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{formatCurrency(
																							facilityInstallment,
																						)}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							textAlign: 'right',
																						}}
																					>
																						{facilityUnitCount ?? 0}
																					</td>
																					<td
																						style={{
																							padding: '10px',
																							border: '1px solid #ccc',
																							whiteSpace: 'nowrap',
																						}}
																					>
																						{facilityPoDetails}
																					</td>
																				</tr>
																			);
																		},
																	);
																})}
															</>
														);
													},
												)}
												<tr
													style={{
														backgroundColor: '#e6f7ff',
														fontWeight: 'bold',
													}}
												>
													<td
														colSpan={3}
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													>
														Total Afiliasi Customer
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalNtf || 0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.totalOutstanding ||
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{formatCurrency(
															customerAffiliateFacilitySummary?.installmentAmount ||
																0,
														)}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
														}}
													>
														{customerAffiliateFacilitySummary?.totalUnitCount ||
															''}
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
														}}
													/>
												</tr>
											</tbody>
										</table>
									</div>
									<div style={{ overflowX: 'auto' }}>
										<table
											style={{
												width: '100%',
												borderCollapse: 'collapse',
												border: '1px solid #ccc',
												fontSize: '14px',
												marginBottom: 20,
											}}
										>
											<tbody>
												<tr style={{ backgroundColor: '#f0f0f0' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														GRAND TOTAL PENGAJUAN (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(
															obligorDetail?.grandTotalApplication,
														)}
													</td>
												</tr>
												<tr style={{ backgroundColor: '#f0f0f0' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														PLAFOND YANG DISETUJUI (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(obligorDetail?.grandTotalPlafon)}
													</td>
												</tr>
												<tr style={{ backgroundColor: '#fffbe6' }}>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															fontWeight: 'bold',
														}}
													>
														SISA PLAFOND (ONE OBLIGOR)
													</td>
													<td
														style={{
															padding: '10px',
															border: '1px solid #ccc',
															textAlign: 'right',
															fontWeight: 'bold',
														}}
													>
														{formatCurrency(
															obligorDetail?.grandTotalRemainingPlafon,
														)}
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<Form.Item
										label="Aging Date"
										labelCol={{ span: 5 }}
										wrapperCol={{ span: 19 }}
									>
										<DatePicker
											style={{ width: 200 }}
											value={dayjs(agingDate)}
											disabled
										/>
									</Form.Item>

									<Divider />

									<div style={{ marginBottom: 20 }}>
										<strong>Ringkasan Detail Transaksi</strong>
									</div>

									<Form.Item wrapperCol={{ span: 24 }}>
										<Table
											bordered
											pagination={false}
											scroll={{ x: 'max-content' }}
											dataSource={
												transactionDetailSummary?.map((record, key) => {
													return {
														...record,
														key,
													};
												}) ?? []
											}
											columns={[
												{
													title: 'No',
													align: 'center',
													fixed: 'left',
													render: (_, __, index) => index + 1,
												},
												{
													title: 'View Detail',
													align: 'center',
													fixed: 'left',
													render: (_, record) => {
														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	setView({
																		type: 'view',
																		data: record,
																	});
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'No. Simulasi',
													render: (_, record) =>
														record.creditSimulationNo ? (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	navigate('SimulationPage', {
																		simulationId: record.creditSimulationId,
																		showResult: true,
																		viewOnly: true,
																		onBack: () => {
																			navigate('VerifyDisbursementPage', {
																				mppId,
																				financingProductId,
																				disbursementId,
																				applicationIds,
																				onBack,
																			});
																		},
																	});
																}}
																style={{ cursor: 'pointer' }}
															>
																{record.creditSimulationNo}
															</a>
														) : (
															'-'
														),
												},
												{
													title: 'No. PO',
													render: (_, record) =>
														record.purchaseOrderNo && record.purchaseOrderId ? (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const documents =
																		await printPurchaseOrderMutation.mutateAsync(
																			{
																				purchaseOrderIds: [
																					record.purchaseOrderId!,
																				],
																			},
																		);

																	openDocumentModal(documents);
																}}
															>
																{record.purchaseOrderNo}
															</a>
														) : (
															'-'
														),
												},
												{
													title: 'Skema Pembiayaan',
													render: (_, record) =>
														record.financingProduct?.value ?? '-',
												},
												{
													title: 'Status PO',
													render: (_, record) =>
														record.purchaseOrderStatus ?? '-',
												},
												{
													title: 'Jenis Barang',
													render: (_, record) => record.assetType ?? '-',
												},
												{
													title: 'Product ID',
													render: (_, record) => record.productId ?? '-',
												},
												{
													title: 'Merk Kendaraan',
													render: (_, record) => record.merk ?? '-',
												},
												{
													title: 'Jumlah Unit',
													render: (_, record) => record.unitCount ?? '-',
												},
												{
													title: 'Total NTF',
													render: (_, record) =>
														record.totalNtf
															? formatCurrency(record.totalNtf)
															: '-',
												},
												{
													title: 'Total Angsuran/bulan',
													render: (_, record) =>
														record.totalInstallment
															? formatCurrency(record.totalInstallment)
															: '-',
												},
												{
													title: 'Total Down Payment',
													render: (_, record) =>
														record.totalDownPayment
															? formatCurrency(record.totalDownPayment)
															: '-',
												},
												{
													title: 'Tipe Asuransi',
													render: (_, record) => record.insuranceType ?? '-',
												},
												{
													title: 'Dokumen SPK',
													fixed: 'right',
													align: 'center',
													colSpan: 2,
													render: (_, record) => {
														if (!record.spkFile) {
															return (
																<DownloadOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();

																	const link = document.createElement('a');
																	link.href = record.spkFile.url;

																	document.body.appendChild(link);

																	link.click();
																	document.body.removeChild(link);
																}}
															>
																<DownloadOutlined />
															</a>
														);
													},
												},
												{
													colSpan: 0,
													fixed: 'right',
													align: 'center',
													render: (_, record) => {
														if (!record.spkFile) {
															return (
																<FileSearchOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={(e) => {
																	e.preventDefault();
																	openPreviewModal(record.spkFile);
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
												{
													title: 'Verifikasi FCOH',
													align: 'center',
													fixed: 'right',
													render: (_, record) => {
														return (
															<Checkbox
																checked={record.isVerified}
																disabled
															/>
														);
													},
												},
											]}
										/>
									</Form.Item>
								</Fragment>
							),
						},
						{
							label: 'Informasi Perjanjian Pembiayaan ',
							children: (
								<Fragment>
									<FleetTitle style={{ marginBottom: 20 }}>
										Tanda Tangan Kontrak
									</FleetTitle>
									<Row
										gutter={20}
										wrap={false}
									>
										<Col span={12}>
											<Row>
												<Col span={12}>
													<b>Pilih Tanda Tangan Kontrak</b>
												</Col>
												<Col span={12}>
													{agreementInformation?.signContractOption || '-'}
												</Col>
											</Row>
										</Col>
										<Col span={12}>
											<Row>
												<Col span={12}>
													<b>No.Perjanjian Pembiayaan</b>
												</Col>
												<Col span={12}>
													{agreementInformation?.agreementNo || '-'}
												</Col>
											</Row>
										</Col>
									</Row>
									<Divider />
									<Row
										gutter={20}
										wrap={false}
									>
										<Col span={12}>
											<Row>
												<Col span={12}>
													<b>Perjanjian Pembiayaan</b>
												</Col>
												<Col span={12}>
													{agreementInformation?.agreementDocument ? (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openPreviewModal(
																	agreementInformation.agreementDocument,
																);
															}}
														>
															View Dokumen
														</a>
													) : (
														'-'
													)}
												</Col>
											</Row>
										</Col>
										<Col span={12}>
											<Row>
												<Col span={12}>
													<b>Tanggal Perjanjian Pembiayaan</b>
												</Col>
												<Col span={12}>
													{agreementInformation?.agreementDate
														? dayjs(agreementInformation.agreementDate).format(
																'DD/MM/YYYY',
															)
														: '-'}
												</Col>
											</Row>
										</Col>
									</Row>
									<Divider />
									<Row
										gutter={20}
										wrap={false}
									>
										<Col span={12}>
											<Row>
												<Col span={12}>
													<b>Surat Pernyataan Data Korporasi</b>
												</Col>
												<Col span={12}>
													{agreementInformation?.commissionerStatementDocument ? (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openPreviewModal(
																	agreementInformation.commissionerStatementDocument,
																);
															}}
														>
															View Dokumen
														</a>
													) : (
														'-'
													)}
												</Col>
											</Row>
										</Col>
										<Col span={12}>
											<Row>
												<Col span={12}>
													<b>Surat Persetujuan Komisaris/RUPS </b>
												</Col>
												<Col span={12}>
													{agreementInformation?.directorStatementDocument ? (
														<a
															href="#"
															onClick={(e) => {
																e.preventDefault();
																openPreviewModal(
																	agreementInformation.directorStatementDocument,
																);
															}}
														>
															View Dokumen
														</a>
													) : (
														'-'
													)}
												</Col>
											</Row>
										</Col>
									</Row>
									<Divider />
								</Fragment>
							),
						},
						...billingItems,
						{
							label:
								'Informasi Dokumen Kesepakatan Realisasi Pencairan Pembiayaan',
							children: (
								<Fragment>
									<Form.Item
										name={fieldName('disbursementRealizationDocuments')}
										wrapperCol={{ span: 24 }}
										initialValue={realizationDocumentList?.documents?.map(
											(record) => {
												return {
													name: record.name,
													document: record.file,
													masterDisbursementDocumentId: record.id,
												};
											},
										)}
										rules={[
											{
												validator: (_, value: { document: FileData }[]) => {
													if (
														groupName === 'FCDVS' &&
														!value.some((i) => i.document)
													) {
														return Promise.reject('Dokument harus diisi');
													}

													return Promise.resolve();
												},
											},
										]}
									>
										<Table
											bordered
											pagination={false}
											dataSource={(
												value?.disbursementRealizationDocuments ?? []
											).map((record, key) => {
												return { ...record, key };
											})}
											columns={[
												{
													title: 'No',
													align: 'center',
													fixed: 'left',
													width: 1,
													render: (_, __, index) => index + 1,
												},
												{
													title: 'Nama Dokumen',
													render: (_, record) => record.name,
												},
												{
													hidden: groupName !== 'FCDVS',
													title: 'Upload Dokumen',
													fixed: 'right',
													align: 'center',
													width: 150,
													render: (_, record, recordIndex) => {
														if (
															uploadMutation.isPending &&
															uploadMutation.variables.fileid.startsWith(
																`${record.masterDisbursementDocumentId}-`,
															)
														) {
															return (
																<LoadingOutlined style={{ color: '#1677ff' }} />
															);
														}

														if (uploadMutation.isPending) {
															return (
																<UploadOutlined style={{ color: '#bababa' }} />
															);
														}

														return (
															<label>
																<UploadOutlined
																	style={{
																		color: '#1677ff',
																		cursor: 'pointer',
																	}}
																/>
																<input
																	type="file"
																	accept=".pdf,.jpg,.jpeg,.png"
																	hidden
																	style={{ display: 'none' }}
																	onChange={async (e) => {
																		const file = e.target.files?.[0];
																		if (!file) return;

																		const fileid = `${record.masterDisbursementDocumentId}-${Date.now().toString()}`;
																		const filepath = `disbursement/${record.masterDisbursementDocumentId}/document`;

																		const url =
																			await uploadMutation.mutateAsync({
																				file,
																				fileid,
																				filepath,
																			});

																		form.setFieldValue(
																			fieldName([
																				'disbursementRealizationDocuments',
																				recordIndex,
																			]),
																			{
																				name: record.name,
																				masterDisbursementDocumentId:
																					record.masterDisbursementDocumentId,
																				document: {
																					url,
																					id: fileid,
																					name: file.name,
																					path: filepath,
																				} as FileData,
																			},
																		);
																	}}
																/>
															</label>
														);
													},
												},
												{
													title: 'View Dokumen',
													fixed: 'right',
													align: 'center',
													width: 130,
													render: (_, record) => {
														if (!record.document) {
															return (
																<FileSearchOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const url =
																		'url' in record.document
																			? (record.document.url as string)
																			: null;

																	if (url) {
																		openPreviewDocumentModal({
																			url,
																		});
																	}
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
											]}
										/>
									</Form.Item>

									<FleetTitle style={{ marginBlock: 20 }}>
										Kondisi Khusus
									</FleetTitle>

									<Form.Item
										name={fieldName('disbursementSpecialConditionDocuments')}
										wrapperCol={{ span: 24 }}
										initialValue={specialConditionDocumentList?.documents?.map(
											(record) => {
												return {
													name: record.name,
													document: record.file,
													masterDisbursementDocumentId: record.id,
												};
											},
										)}
									>
										<Table
											bordered
											pagination={false}
											dataSource={(
												value?.disbursementSpecialConditionDocuments ?? []
											).map((record, key) => {
												return { ...record, key };
											})}
											columns={[
												{
													title: 'No',
													align: 'center',
													fixed: 'left',
													width: 1,
													render: (_, __, index) => index + 1,
												},
												{
													title: 'Nama Dokumen',
													render: (_, record) => record.name,
												},
												{
													hidden: groupName !== 'FCDVS',
													title: 'Upload Dokumen',
													fixed: 'right',
													align: 'center',
													width: 150,
													render: (_, record, recordIndex) => {
														if (
															uploadMutation.isPending &&
															uploadMutation.variables.fileid.startsWith(
																`${record.masterDisbursementDocumentId}-`,
															)
														) {
															return (
																<LoadingOutlined style={{ color: '#1677ff' }} />
															);
														}

														if (uploadMutation.isPending) {
															return (
																<UploadOutlined style={{ color: '#bababa' }} />
															);
														}

														return (
															<label>
																<UploadOutlined
																	style={{
																		color: '#1677ff',
																		cursor: 'pointer',
																	}}
																/>
																<input
																	type="file"
																	hidden
																	style={{ display: 'none' }}
																	onChange={async (e) => {
																		const file = e.target.files?.[0];
																		if (!file) return;

																		const fileid = `${record.masterDisbursementDocumentId}-${Date.now().toString()}`;
																		const filepath = `disbursement/${record.masterDisbursementDocumentId}/document`;

																		const url =
																			await uploadMutation.mutateAsync({
																				file,
																				fileid,
																				filepath,
																			});

																		form.setFieldValue(
																			fieldName([
																				'disbursementSpecialConditionDocuments',
																				recordIndex,
																			]),
																			{
																				name: record.name,
																				masterDisbursementDocumentId:
																					record.masterDisbursementDocumentId,
																				document: {
																					url,
																					id: fileid,
																					name: file.name,
																					path: filepath,
																				} as FileData,
																			},
																		);
																	}}
																/>
															</label>
														);
													},
												},
												{
													title: 'View Dokumen',
													fixed: 'right',
													align: 'center',
													width: 130,
													render: (_, record) => {
														if (!record.document) {
															return (
																<FileSearchOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const url =
																		'url' in record.document
																			? (record.document.url as string)
																			: null;

																	if (url) {
																		openPreviewDocumentModal({
																			url,
																		});
																	}
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
											]}
										/>
									</Form.Item>

									<FleetTitle style={{ marginBlock: 20 }}>
										Dokumen Persyaratan Golive dengan Status To Be Obtained
										(TBO)
									</FleetTitle>

									<Form.Item
										name={fieldName('disbursementGoliveConditionDocuments')}
										wrapperCol={{ span: 24 }}
										initialValue={goliveConditionDocumentList?.documents?.map(
											(record) => {
												return {
													name: record.name,
													document: record.file,
													masterDisbursementDocumentId: record.id,
												};
											},
										)}
									>
										<Table
											bordered
											pagination={false}
											dataSource={(
												value?.disbursementGoliveConditionDocuments ?? []
											).map((record, key) => {
												return { ...record, key };
											})}
											columns={[
												{
													title: 'No',
													align: 'center',
													fixed: 'left',
													width: 1,
													render: (_, __, index) => index + 1,
												},
												{
													title: 'Nama Dokumen',
													render: (_, record) => record.name,
												},
												{
													hidden: groupName !== 'FCDVS',
													title: 'Upload Dokumen',
													fixed: 'right',
													align: 'center',
													width: 150,
													render: (_, record, recordIndex) => {
														if (
															uploadMutation.isPending &&
															uploadMutation.variables.fileid.startsWith(
																`${record.masterDisbursementDocumentId}-`,
															)
														) {
															return (
																<LoadingOutlined style={{ color: '#1677ff' }} />
															);
														}

														if (uploadMutation.isPending) {
															return (
																<UploadOutlined style={{ color: '#bababa' }} />
															);
														}

														return (
															<label>
																<UploadOutlined
																	style={{
																		color: '#1677ff',
																		cursor: 'pointer',
																	}}
																/>
																<input
																	type="file"
																	hidden
																	style={{ display: 'none' }}
																	onChange={async (e) => {
																		const file = e.target.files?.[0];
																		if (!file) return;

																		const fileid = `${record.masterDisbursementDocumentId}-${Date.now().toString()}`;
																		const filepath = `disbursement/${record.masterDisbursementDocumentId}/document`;

																		const url =
																			await uploadMutation.mutateAsync({
																				file,
																				fileid,
																				filepath,
																			});

																		form.setFieldValue(
																			fieldName([
																				'disbursementGoliveConditionDocuments',
																				recordIndex,
																			]),
																			{
																				name: record.name,
																				masterDisbursementDocumentId:
																					record.masterDisbursementDocumentId,
																				document: {
																					url,
																					id: fileid,
																					name: file.name,
																					path: filepath,
																				} as FileData,
																			},
																		);
																	}}
																/>
															</label>
														);
													},
												},
												{
													title: 'View Dokumen',
													fixed: 'right',
													align: 'center',
													width: 130,
													render: (_, record) => {
														if (!record.document) {
															return (
																<FileSearchOutlined
																	style={{ color: '#bababa' }}
																/>
															);
														}

														return (
															<a
																href="#"
																onClick={async (e) => {
																	e.preventDefault();
																	const url =
																		'url' in record.document
																			? (record.document.url as string)
																			: null;

																	if (url) {
																		openPreviewDocumentModal({
																			url,
																		});
																	}
																}}
															>
																<FileSearchOutlined />
															</a>
														);
													},
												},
											]}
										/>
									</Form.Item>
								</Fragment>
							),
						},
					]}
				/>

				<Divider />

				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Approval History</FleetTitle>
				</div>
				<Table
					bordered
					pagination={false}
					dataSource={
						approvalHistory?.map((history, index) => ({
							...history,
							key: index,
						})) ?? []
					}
					columns={[
						{
							title: 'No',
							align: 'center',
							render: (_, __, index) => index + 1,
						},
						{
							title: 'Name',
							render: (_, record) => record.name,
						},
						{
							title: 'Date',
							align: 'center',
							render: (_, record) =>
								record.date && dayjs(record.date).format('DD/MM/YYYY'),
						},
						{
							title: 'Process',
							align: 'center',
							render: (_, record) => record.process,
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => record.status,
						},
						{
							title: 'Approval Notes',
							align: 'center',
							render: (_, record) => record.notes,
						},
					]}
				/>

				<Divider />

				{(groupName === 'FCOH' || groupName === 'FCOM') && (
					<Fragment>
						<Row gutter={40}>
							<Col span={12}>
								<Form.Item
									name={fieldName('action')}
									rules={[rule.required()]}
									label="Approval"
								>
									<Select
										options={[
											{
												label: 'Approve',
												value: 'approve',
											},
											{
												label: 'Return',
												value: 'return',
											},
										]}
										allowClear
										style={{ width: 300 }}
									/>
								</Form.Item>
							</Col>
							{(groupName === 'FCOM' || groupName === 'FCOH') &&
								value?.action === 'return' && (
									<Col span={12}>
										<Form.Item
											name="returnUser"
											rules={[rule.required()]}
											label="Return to"
											getValueFromEvent={(_, option) => option?.data}
											getValueProps={(value) => {
												if (!value) return {};
												return {
													value: {
														value: value.id,
														label: value.value,
														data: value,
													},
												};
											}}
										>
											<Select
												options={returnUsers.map((user) => {
													return {
														label: user.value,
														value: user.id,
														data: user,
													};
												})}
												allowClear
												style={{ width: 300 }}
											/>
										</Form.Item>
									</Col>
								)}
						</Row>

						<Divider />
					</Fragment>
				)}

				{!isViewOnly && (
					<>
						<Form.Item
							name={fieldName('notes')}
							rules={[rule.required()]}
							label="Notes"
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
						>
							<Input.TextArea rows={4} />
						</Form.Item>

						<Button
							type="primary"
							htmlType="submit"
							style={{
								display: 'block',
								width: 200,
								marginLeft: 'auto',
								marginTop: 40,
							}}
						>
							SAVE
						</Button>
					</>
				)}
			</Form>
		);
	};

	const renderViewTransaction = () => {
		const data = view.type === 'view' ? view.data : undefined;
		if (!data) return null;

		const totalUnit = data.unitCount ?? 0;

		// Calculate total NTF from transaction data
		const totalNtf =
			transactionDetailSummary?.reduce((acc, transaction) => {
				return acc + (transaction.totalNtf ?? 0);
			}, 0) ?? 0;

		// Calculate KKB reward based on totalNtf and percentage
		const kkbRewardPercentage = data.kkbRewardPercentage ?? 0;
		const totalKkbReward = (totalNtf * kkbRewardPercentage) / 100;

		const kkbRewardPerUnit =
			(totalUnit ?? 0) > 0 ? totalKkbReward / (totalUnit ?? 1) : 0;
		const kkbRewardAllUnit = totalKkbReward;

		const getDisabledRewardOrderSources = () => {
			return [
				{ id: 3, value: 'Captive JF Commercial' },
				// { id: 4, value: 'Captive JF Corporate' },
				// Note: Captive Non-JF (id: 2) should have rewards enabled
				// Note: Captive MTF (id: 5) should have rewards enabled
			];
		};

		// Check if current order source should have rewards disabled
		const shouldDisableReward = () => {
			if (!orderSource) return false;

			const disabledSources = getDisabledRewardOrderSources();
			return disabledSources.some(
				(source) =>
					source.id === orderSource.id || source.value === orderSource.value,
			);
		};

		const disableReward = shouldDisableReward();

		return (
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				labelCol={{ span: 10 }}
				wrapperCol={{ span: 14, style: { alignSelf: 'center' } }}
			>
				<Tabs
					activeKey={'1'}
					items={[
						{
							key: '1',
							label: 'Rincian Detail Transaksi',
							children: (
								<Fragment>
									<div style={{ marginBottom: 20 }}>
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item label="Jenis Pembiayaan">
													{data?.financingProduct?.value ?? '-'}
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item label="Jenis Asset">
													{data.assetType ?? '-'}
												</Form.Item>
											</Col>
										</Row>
										<Divider />
										<Row gutter={40}>
											<Col span={12}>
												<Form.Item label="Segment Industri">
													{data?.segmentIndustry ?? '-'}
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item label="Tujuan Pembiayaan">
													<Input
														disabled
														value={data.financialPurpose?.value}
													/>
												</Form.Item>
											</Col>
										</Row>
									</div>
								</Fragment>
							),
						},
					]}
				/>
				<Collapse
					defaultActiveKey={disableReward ? [0, 2] : [0, 1, 2]}
					items={[
						{
							label: 'Informasi Aset',
							children: (
								<Fragment>
									<Row
										align="middle"
										style={{ marginBottom: 20, gap: 40 }}
									>
										<Col flex="none">
											<strong>Total Jumlah Unit</strong>
										</Col>
										<Col flex="auto">
											<span>{totalUnit} Unit</span>
										</Col>
									</Row>
									<Form.Item wrapperCol={{ span: 24 }}>
										<Table
											bordered
											pagination={false}
											scroll={{ x: 'max-content' }}
											dataSource={
												data.assetInformations.map((record, key) => ({
													...record,
													key,
												})) ?? []
											}
											columns={[
												{
													title: 'No',
													align: 'center',
													width: 1,
													render: (_, __, index) => index + 1,
												},
												{
													title: 'Nama STNK/BPKB/Invoice',
													render: (_, record) => record.bpkbOwnerName ?? '-',
												},
												{
													title: 'Jumlah Unit',
													render: (_, record) =>
														('unitCount' in record
															? record.unitCount
															: record.unitAmount) ?? '-',
												},
												{
													title: 'Pengguna Aset',
													render: (_, record) => record.assetUser ?? '-',
												},
												{
													title: 'Nama Bouwheer',
													render: (_, record) => record.bouwheer?.name ?? '-',
												},
												{
													title: 'Bouwheer Type',
													render: (_, record) => record.bouwheer?.type ?? '-',
												},
												{
													title: 'Industri Bouwheer',
													render: (_, record) =>
														record.bouwheer?.industrySector?.sectorLevel5 ??
														'-',
												},
												{
													title: 'Kota',
													render: (_, record) =>
														record.address?.cityName ?? '-',
												},
											]}
										/>
									</Form.Item>
								</Fragment>
							),
						},
						{
							label: 'Reward',
							collapsible: disableReward ? 'disabled' : undefined,
							children: !disableReward && (
								<Fragment>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Apakah ada reward KKB?">
												<Input
													disabled
													value={
														data.kkbRewardAvailableId === 1
															? 'Ya'
															: data.kkbRewardAvailableId === 2
																? 'Tidak'
																: '-'
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Nama Pemberi Referral">
												{data.kkbReferralName ?? '-'}
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Apakah ada reward KKB?">
												<InputNumber
													disabled
													value={data.kkbRewardPercentage}
													suffix="%"
													controls={false}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
									</Row>
									{/* <Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Apakah ada refund dealer?">
												<Select
													disabled
													value={data.hasDealerRefund}
													options={[
														{
															label: 'Ya',
															value: true,
														},
														{
															label: 'Tidak',
															value: false,
														},
													]}
													placeholder="Pilih ya/tidak"
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Jumlah Refund Dealer (Rp)">
												<InputNumber
													disabled
													value={data.dealerRefundAmount ?? '-'}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
									</Row> */}
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item
												label={
													<Fragment>
														<span>
															Jumlah Reward KKB (Rp)
															<br />
															<small style={{ fontWeight: 'normal' }}>
																Per Unit
															</small>
														</span>
													</Fragment>
												}
											>
												<Input
													disabled
													value={
														kkbRewardPerUnit
															? formatCurrency(kkbRewardPerUnit)
															: ''
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label={
													<Fragment>
														<span>
															Jumlah Reward KKB (Rp)
															<br />
															<small style={{ fontWeight: 'normal' }}>
																All Unit
															</small>
														</span>
													</Fragment>
												}
											>
												<Input
													disabled
													value={
														kkbRewardAllUnit
															? formatCurrency(kkbRewardAllUnit)
															: ''
													}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Fragment>
							),
						},
						{
							label: 'Cara Pembayaran Angsuran',
							children: (
								<Fragment>
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Way Of Payment">
												<Input
													disabled
													value={data.wayOfPayment?.value ?? '-'}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Nama Bank">
												<Input
													value={data.bank?.value ?? '-'}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Nama Cabang">
												<Input
													disabled
													value={data.bankBranch ?? '-'}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={40}>
										<Col span={12}>
											<Form.Item label="Nomor Rekening">
												<Input
													value={data.bankAccountNumber ?? '-'}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Form.Item
										label="Notes"
										labelCol={{ span: 5 }}
										wrapperCol={{ span: 19 }}
									>
										<Input.TextArea
											rows={4}
											disabled
											value={data.wayOfPaymentNotes ?? '-'}
										/>
									</Form.Item>
									<Divider />
								</Fragment>
							),
						},
					]}
				/>
				<div
					style={{
						display: 'flex',
						gap: 20,
						justifyContent: 'flex-end',
						marginTop: 20,
					}}
				>
					<Button
						style={{
							display: 'block',
							width: 200,
						}}
						onClick={() => {
							setView({
								type: 'main',
							});
						}}
					>
						BACK
					</Button>
				</div>
			</Form>
		);
	};

	return (
		<Fragment>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				{typeof onBack === 'function' && (
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => onBack?.()}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
				)}
				<div style={{ flex: 1 }}>
					<FleetTitle>Detail Verifikasi Dokumen Pencairan</FleetTitle>
				</div>
			</div>
			<DataRow
				style={{ marginBottom: 20 }}
				items={[
					[
						[
							'Nama Customer',
							customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(customerId);
									}}
								>
									{customerName}
								</a>
							) : (
								'-'
							),
						],
						['No.MKL', mklNo ?? '-'],
					],
					[
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
						[
							'No.SPF ',
							spfNo && spfId ? (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('SPFPage', {
											prospectId: prospectId!,
											spfId,
											napResolutionId: napResolutionId!,
											viewOnly: true,
											onBack: () => {
												navigate('VerifyDisbursementPage', {
													mppId,
													financingProductId,
													disbursementId,
													applicationIds,
													onBack,
												});
											},
										});
									}}
								>
									{spfNo}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						['Portofolio Guideline', portfolioGuideline],
						['Kelas Industri', industryClass],
					],
					[
						[
							'Summary NAP',
							napPlafonTypeId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!prospectId) return;

										if (napPlafonTypeId === 1) {
											napNJFModal.open({
												prospectId,
											});
										}

										if (napPlafonTypeId === 2) {
											napJFModal.open({
												prospectId,
											});
										}
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
						['Ultimate Owner', ultimateOwner],
					],
					[
						[
							'View Kolektabilitas',
							slikInquiryId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										kolektabilitasModal.open({
											slikInquiryId,
										});
									}}
								>
									View Kolektabilitas
								</a>
							) : (
								'-'
							),
						],
						[
							'Summary Risalah',
							resolutionFile ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(resolutionFile);
									}}
								>
									View Dokumen
								</a>
							) : (
								'-'
							),
						],
					],
					[
						['FCDVS ', fcdvsName ?? '-'],
						['FCAS ', fcasName ?? '-'],
					],
				]}
			/>
			{renderMainForm()}
			{renderViewTransaction()}

			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			<CovenantModal
				{...covenantModal}
				covenantStatusOptions={covenantStatusOptions}
			/>
			<KolektabilitasModal {...kolektabilitasModal} />
			<UploadDocumentModal {...uploadDocumentModal} />
		</Fragment>
	);
};

export default VerifyDisbursementPage;
